import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Send, Loader2, EyeOff, Crown, Volume2, VolumeX } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIDE_KEY = "airpog_hide_pog_fab";
const HINT_KEY = "airpog_pog_swipe_hint";
const SOUND_KEY = "airpog_pogbot_sound";

// Cute two-tone robot beep via Web Audio (no asset needed). Silently no-ops
// if the browser hasn't had a user gesture yet (autoplay policy).
const playBeep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === "suspended") { ctx.close(); return; }
    const note = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + start);
      o.stop(ctx.currentTime + start + dur + 0.05);
    };
    note(880, 0, 0.12);
    note(1318, 0.14, 0.16);
    setTimeout(() => ctx.close(), 600);
  } catch { /* ignore */ }
};

const PERSONAS = {
  pogbot: {
    persona: "pogbot",
    name: "POG BOT",
    sub: "PROTECTOR OF POG CASTLE · online",
    img: "/pogbot.webp",
    sidKey: "airpog_pogbot_chat_sid",
    greeting: "BEEP-BOOP, POG'R! 🤖 I'm POG BOT — PROTECTOR OF POG CASTLE & THE POGZ R US FACTORY. When I'm not warding off MONSTER POGZ, I answer questions! Ask me how to stack POG BUCKS, crack packs, win games… POG AWAY!",
    error: "BZZZT — MONSTER POGZ jammed my circuits! Give it another POG! 🤖",
    placeholder: "Ask POG BOT anything…",
  },
  pogfather: {
    persona: "pogfather",
    name: "MR. POG",
    sub: "THE POGFATHER · online",
    img: "/airpogs/pogfather.webp",
    sidKey: "airpog_pog_chat_sid",
    greeting: "Welcome to THE POGZ FACTORY, POG'r! 🎩 I'm MR. POG. Ask me anything — how to stack POG BUCKS, snag the Golden Ticket, win the games... go on, POG it!",
    error: "MR. POG got distracted by a shiny legendary. Give it another POG! 🎩",
    placeholder: "Ask Mr. POG anything…",
  },
};

const getSid = (key) => {
  try {
    let s = localStorage.getItem(key);
    if (!s) { s = "pog_" + Math.random().toString(36).slice(2, 12); localStorage.setItem(key, s); }
    return s;
  } catch { return "pog_" + Math.random().toString(36).slice(2, 12); }
};

const useChat = (active, p) => {
  const [msgs, setMsgs] = useState([{ role: "assistant", text: p.greeting }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sidRef = useRef(null);
  if (!sidRef.current) sidRef.current = getSid(p.sidKey);

  useEffect(() => {
    if (!active) return;
    axios.get(`${API}/ask-pog/${sidRef.current}`).then((r) => {
      if (r.data.messages?.length) setMsgs(r.data.messages.map((m) => ({ role: m.role, text: m.text })));
    }).catch(() => {});
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ask-pog`, { session_id: sidRef.current, message: text, persona: p.persona });
      setMsgs((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (err) {
      const detail = err?.response?.status === 429 ? err.response.data?.detail : null;
      setMsgs((m) => [...m, { role: "assistant", text: detail || p.error }]);
    } finally {
      setBusy(false);
    }
  };
  return { msgs, input, setInput, busy, send };
};

const ChatBody = ({ active, p }) => {
  const { msgs, input, setInput, busy, send } = useChat(active, p);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const isBot = p.persona === "pogbot";
  return (
    <>
      <div data-testid="ask-pog-messages" className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-snug ${m.role === "user" ? "bg-sky-600 text-white rounded-br-sm" : `bg-slate-800 text-slate-100 rounded-bl-sm border ${isBot ? "border-cyan-300/20" : "border-amber-300/20"}`}`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className={`rounded-2xl px-3.5 py-2 bg-slate-800 border ${isBot ? "border-cyan-300/20 text-cyan-200" : "border-amber-300/20 text-amber-200"}`}><Loader2 className="w-4 h-4 animate-spin" /></div></div>
        )}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-slate-800 shrink-0 flex items-center gap-2">
        <input
          data-testid="ask-pog-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={p.placeholder}
          className={`flex-1 h-11 rounded-full bg-slate-800 border border-slate-700 px-4 text-sm text-white placeholder:text-slate-500 focus:outline-none ${isBot ? "focus:border-cyan-400" : "focus:border-amber-400"}`}
        />
        <button data-testid="ask-pog-send" onClick={send} disabled={busy || !input.trim()} className={`w-11 h-11 rounded-full text-white flex items-center justify-center disabled:opacity-50 shrink-0 ${isBot ? "bg-gradient-to-r from-cyan-400 to-blue-600" : "bg-gradient-to-r from-amber-400 to-yellow-500 !text-amber-950"}`}>
          <Send className="w-4 h-4" />
        </button>
      </div>
    </>
  );
};

const Header = ({ p, onClose, onHide, soundOn, onToggleSound }) => {
  const isBot = p.persona === "pogbot";
  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b shrink-0 ${isBot ? "bg-gradient-to-r from-cyan-500/25 to-blue-600/20 border-cyan-300/30" : "bg-gradient-to-r from-amber-500/30 to-fuchsia-500/20 border-amber-300/30"}`}>
      <img src={p.img} alt="" className={`w-9 h-9 rounded-full border object-contain ${isBot ? "border-cyan-300 bg-slate-900 p-0.5" : "border-amber-300 object-cover"}`} />
      <div className="flex-1 min-w-0">
        <p className="font-heading font-extrabold text-sm leading-tight text-white">{p.name}</p>
        <p className={`text-[10px] font-bold uppercase tracking-wider ${isBot ? "text-cyan-200" : "text-amber-200"}`}>{p.sub}</p>
      </div>
      {onToggleSound && (
        <button data-testid="pogbot-sound-toggle" onClick={onToggleSound} title={soundOn ? "Beeps: on — tap to mute" : "Beeps: off — tap to unmute"} className={`w-8 h-8 rounded-lg hover:bg-slate-700/60 flex items-center justify-center ${soundOn ? "text-cyan-200" : "text-slate-500"}`}>
          {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      )}
      {onHide && (
        <button data-testid="ask-pog-hide" onClick={onHide} title={`Hide ${p.name}`} className="h-8 px-2.5 rounded-lg hover:bg-slate-700/60 flex items-center gap-1 text-[11px] font-bold text-slate-300">
          <EyeOff className="w-3.5 h-3.5" /> Hide
        </button>
      )}
      {onClose && (
        <button data-testid="ask-pog-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-700/60 flex items-center justify-center text-slate-300"><X className="w-4 h-4" /></button>
      )}
    </div>
  );
};

// Permanent, full-height embedded chat for the dedicated "Ask Mr. POG" page.
export const AskPogPanel = () => (
  <div className="flex flex-col h-full rounded-3xl overflow-hidden border-2 border-amber-300/50 bg-slate-950">
    <Header p={PERSONAS.pogfather} />
    <ChatBody active={true} p={PERSONAS.pogfather} />
  </div>
);

// Floating, dismissible POG BOT widget on the map. "Hide" persists everywhere;
// POG BOT can escalate POG'Rs to MR. POG himself on the dedicated /ask-pog page.
export const AskPog = ({ fabClass = "bottom-5 left-5", panelClass = "bottom-5 left-5", tips = [] }) => {
  const [open, setOpen] = useState(false);
  const [swiped, setSwiped] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [tip, setTip] = useState(null);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) !== "0"; } catch { return true; }
  });
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;
  const toggleSound = () => {
    setSoundOn((v) => {
      try { localStorage.setItem(SOUND_KEY, v ? "0" : "1"); } catch { /* ignore */ }
      return !v;
    });
  };
  const tipIdxRef = useRef(0);
  const tipsRef = useRef(tips);
  tipsRef.current = tips;
  const draggingRef = useRef(false);
  const engagedRef = useRef(false);
  const navigate = useNavigate();
  const p = PERSONAS.pogbot;
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(HIDE_KEY) === "1"; } catch { return false; }
  });

  const hideForever = () => {
    try { localStorage.setItem(HIDE_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
    setHidden(true);
  };

  const dismissHint = () => {
    setShowHint(false);
    try { localStorage.setItem(HINT_KEY, "1"); } catch { /* ignore */ }
  };

  const engage = () => { engagedRef.current = true; dismissHint(); setOpen(true); };

  // Allow other parts of the app (e.g. a menu item) to re-summon POG BOT.
  useEffect(() => {
    const bringBack = () => {
      try { localStorage.removeItem(HIDE_KEY); } catch { /* ignore */ }
      engagedRef.current = true;
      setHidden(false);
      setSwiped(false);
    };
    window.addEventListener("airpog:showpog", bringBack);
    return () => window.removeEventListener("airpog:showpog", bringBack);
  }, []);

  // One-time "swipe to dismiss" hint, shown briefly on first visit.
  useEffect(() => {
    try { if (localStorage.getItem(HINT_KEY)) return; } catch { return; }
    const t1 = setTimeout(() => setShowHint(true), 1800);
    const t2 = setTimeout(() => dismissHint(), 7000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Auto-hide after ~60s — but only if the POG'r never opened his chat.
  useEffect(() => {
    const t = setTimeout(() => { if (!engagedRef.current && !open) setSwiped(true); }, 60000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Proactive beeps — POG BOT surfaces a contextual tip shortly after load, then every ~2 min (max 3/session).
  useEffect(() => {
    if (hidden) return;
    let shown = 0;
    let tipT = null;
    const showNext = () => {
      const list = tipsRef.current;
      if (!list.length || shown >= 3) return;
      setTip(list[tipIdxRef.current % list.length]);
      tipIdxRef.current += 1;
      shown += 1;
      if (soundRef.current) playBeep();
      clearTimeout(tipT);
      tipT = setTimeout(() => setTip(null), 9000);
    };
    const t1 = setTimeout(showNext, 15000);
    const iv = setInterval(showNext, 120000);
    return () => { clearTimeout(t1); clearInterval(iv); clearTimeout(tipT); };
  }, [hidden]);

  if (hidden) return null;

  return (
    <>
      <div className={`fixed ${fabClass} z-[1500]`}>
        <AnimatePresence>
          {showHint && !swiped && (
            <motion.div
              data-testid="ask-pog-swipe-hint"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
              className="absolute bottom-full right-0 mb-2 whitespace-nowrap rounded-full bg-slate-900/90 backdrop-blur-md ring-1 ring-cyan-300/30 px-3 py-1.5 text-[11px] font-heading font-bold text-cyan-200 shadow-lg pointer-events-none"
            >
              👋 Swipe me away to hide
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {tip && !swiped && !open && !showHint && (
            <motion.div
              data-testid="pogbot-tip-bubble"
              initial={{ opacity: 0, y: 8, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="absolute bottom-full right-0 mb-2 w-56 max-w-[70vw] rounded-2xl rounded-br-sm bg-slate-900/95 backdrop-blur-md ring-1 ring-cyan-300/40 shadow-xl shadow-cyan-500/20"
            >
              <button
                onClick={() => { setTip(null); engage(); }}
                className="w-full text-left px-3.5 py-2.5 pr-8 text-[12px] font-bold leading-snug text-cyan-100"
              >
                {tip}
              </button>
              <button
                data-testid="pogbot-tip-mute"
                onClick={(e) => { e.stopPropagation(); toggleSound(); }}
                title={soundOn ? "Mute POG BOT beeps" : "Unmute POG BOT beeps"}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-lg hover:bg-slate-700/70 flex items-center justify-center text-slate-400"
              >
                {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {!swiped && (
            <motion.button
              data-testid="ask-pog-fab"
              drag
              dragSnapToOrigin
              dragElastic={0.7}
              dragMomentum={false}
              onDragStart={() => { draggingRef.current = true; dismissHint(); }}
              onDragEnd={(e, info) => {
                const dist = Math.hypot(info.offset.x, info.offset.y);
                if (dist > 85) setSwiped(true);
                setTimeout(() => { draggingRef.current = false; }, 60);
              }}
              onClick={() => { if (!draggingRef.current) engage(); }}
              whileTap={{ scale: 0.95 }}
              whileDrag={{ scale: 1.12 }}
              exit={{ opacity: 0, scale: 0.4, transition: { duration: 0.25 } }}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-cyan-300 shadow-xl overflow-hidden touch-none cursor-grab active:cursor-grabbing bg-gradient-to-b from-sky-500/90 to-blue-800"
              style={{ boxShadow: "0 0 24px rgba(34,211,238,.55)" }}
              aria-label="Ask POG BOT — tap to chat, swipe to dismiss"
            >
              <img src={p.img} alt="Ask POG BOT" className="w-full h-full object-contain p-1.5 pointer-events-none" draggable={false} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ask-pog-panel"
            initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            className={`fixed ${panelClass} z-[1600] w-[calc(100vw-2.5rem)] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-3xl overflow-hidden border-2 border-cyan-300/50 bg-slate-950 shadow-2xl`}
          >
            <Header p={p} onClose={() => setOpen(false)} onHide={hideForever} soundOn={soundOn} onToggleSound={toggleSound} />
            <button
              data-testid="ask-pog-see-mrpog"
              onClick={() => { setOpen(false); navigate("/ask-pog"); }}
              className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-400/10 border-b border-amber-300/20 text-[11px] font-heading font-bold text-amber-200 hover:bg-amber-400/20 transition-colors"
            >
              <Crown className="w-3.5 h-3.5" /> Need the boss? Talk to MR. POG himself →
            </button>
            <ChatBody active={open} p={p} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
